import { zodResolver } from '@hookform/resolvers/zod'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import PhoneInput from 'react-phone-number-input'
import { useLocation, useNavigate } from 'react-router-dom'
import * as z from 'zod'
import 'react-phone-number-input/style.css'
import * as Sentry from '@sentry/react'
import React from 'react'

import GooglePlacesSelect from '../components/GooglePlacesSelect'
import MultiSelect from '../components/MultiSelect'
import Select from '../components/Select'
import Stepper from '../components/Stepper'
import AcceptModal from '../components/registration/AcceptModal'
import RejectModal from '../components/registration/RejectModal'
import TermsConfirmationModal from '../components/registration/TermsConfirmationModal'
import VerifyEmailModal from '../components/registration/VerifyEmailModal'
import { Button } from '../components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form'
import { Input } from '../components/ui/input'
import { useUser } from '../contexts/UserContext'
import { sendEmailOtpService } from '../services/authService'
import type { ErrorData } from '../services/fetch'
import { userService } from '../services/userService'
import { StakeholderType, type TeamInvite } from '../types/user'
import { validatePassword } from '../utils/passwordValidation'

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_KEY_HERE',
)

const STAKEHOLDER_OPTIONS = [
  { value: StakeholderType.BROKER, label: 'Broker' },
  { value: StakeholderType.SUB_HAULER, label: 'Sub Hauler' },
  { value: StakeholderType.CONTRACTOR, label: 'Contractor' },
]

// Company Types enum matching backend
enum CompanyType {
  S_CORP = 'S_CORP',
  LLC = 'LLC',
  C_CORP = 'C_CORP',
  INDIVIDUAL = 'INDIVIDUAL',
  PARTNERSHIP = 'PARTNERSHIP',
}

const COMPANY_TYPE_OPTIONS = [
  { value: CompanyType.S_CORP as string, label: 'S Corporation' },
  { value: CompanyType.C_CORP as string, label: 'C Corporation' },
  {
    value: CompanyType.LLC as string,
    label: 'Limited Liability Company (LLC)',
  },
  { value: CompanyType.PARTNERSHIP as string, label: 'Partnership' },
  {
    value: CompanyType.INDIVIDUAL as string,
    label: 'Sole Proprietorship (Individual)',
  },
]

const createRegisterSchema = (isTeamInvite: boolean) => {
  const baseSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(1, 'Password is required'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    phoneNumber: z
      .string()
      .min(1, 'Phone number is required')
      .refine((phone) => {
        // For US numbers starting with +1, remove the country code before counting
        let phoneToCheck = phone
        if (phone.startsWith('+1')) {
          phoneToCheck = phone.substring(2) // Remove '+1'
        }
        const digitCount = phoneToCheck.replace(/\D/g, '').length
        console.log(
          `Phone validation: "${phone}" -> "${phoneToCheck}" -> ${digitCount} digits`,
        )
        return digitCount === 10
      }, 'Please enter exactly 10 digits'),
    stakeholderTypes: isTeamInvite
      ? z.array(z.nativeEnum(StakeholderType)).optional()
      : z
          .array(z.nativeEnum(StakeholderType))
          .min(1, 'Please select at least one company type'),
    companyName: isTeamInvite
      ? z.string().optional()
      : z.string().min(1, 'Company name is required'),
    corporateAddress: isTeamInvite
      ? z.string().optional()
      : z.string().min(1, 'Corporate address is required'),
    companyType: isTeamInvite
      ? z.nativeEnum(CompanyType).optional()
      : z.nativeEnum(CompanyType, {
          required_error: 'Company type is required',
        }),
    // Conditional fields
    ccn: z.string().optional(),
    eid: z.string().optional(),
    licenseNumber: z.string().optional(),
    permitNumber: z.string().optional(),
    zoningNumber: z.string().optional(),
  })

  // Add validation for all schemas
  return baseSchema.superRefine((data, ctx) => {
    // Password validation with user context
    const passwordValidation = validatePassword(data.password, {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
    })

    if (!passwordValidation.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          passwordValidation.message || 'Password does not meet requirements',
        path: ['password'],
      })
    }

    // Skip company field validation for team invites
    if (isTeamInvite) {
      return
    }

    // Add conditional validation for non-team invite registrations
    if (
      data.companyType === CompanyType.C_CORP ||
      data.companyType === CompanyType.S_CORP
    ) {
      if (!data.ccn || data.ccn.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CCN is required for corporations',
          path: ['ccn'],
        })
      }
    }

    if (
      data.companyType === CompanyType.LLC ||
      data.companyType === CompanyType.PARTNERSHIP
    ) {
      if (!data.eid || data.eid.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'EID is required for LLCs and partnerships',
          path: ['eid'],
        })
      }
    }

    if (data.companyType === CompanyType.INDIVIDUAL) {
      if (!data.licenseNumber || data.licenseNumber.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'License number is required for sole proprietorships',
          path: ['licenseNumber'],
        })
      }
      if (!data.permitNumber || data.permitNumber.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Permit number is required for sole proprietorships',
          path: ['permitNumber'],
        })
      }
      if (!data.zoningNumber || data.zoningNumber.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Zoning number is required for sole proprietorships',
          path: ['zoningNumber'],
        })
      }
    }
  })
}

const baseRegisterSchema = createRegisterSchema(false)

type RegisterFormData = z.infer<typeof baseRegisterSchema>

type ErrorResponse = {
  errors?: { message: string }[]
  message?: string
  detail?: string
}

type APIError = {
  response?: {
    status?: number
    data?: ErrorResponse
  }
}

type ExtendedErrorResponse = {
  response?: {
    status?: number
    data?: {
      errors: ErrorData[]
    }
  }
}

// PaymentValidationForm component for payment method collection and validation
const PaymentValidationForm = ({
  formData,
  onPaymentValidated,
  onFormReadyChange,
}: {
  formData: RegisterFormData
  onPaymentValidated: (paymentMethodId: string) => void
  onFormReadyChange: (
    isReady: boolean,
    validationFunction: () => Promise<string | false>,
  ) => void
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [paymentError, setPaymentError] = useState<string>('')

  const handlePaymentValidation = React.useCallback(async (): Promise<
    string | false
  > => {
    if (!stripe || !elements) {
      console.error('Stripe or elements not loaded')

      Sentry.captureMessage(
        'Stripe or elements not loaded for payment validation',
        {
          level: 'error',
          tags: {
            error_type: 'payment_stripe_not_ready',
            user_email: formData.email,
          },
          extra: {
            has_stripe: !!stripe,
            has_elements: !!elements,
            step: 'payment_validation',
          },
        },
      )

      setPaymentError('Payment system not ready. Please try again.')
      return false
    }

    setPaymentError('')

    Sentry.addBreadcrumb({
      message: 'Payment validation started',
      category: 'registration',
      level: 'info',
      data: {
        email: formData.email,
        step: 'payment_validation',
      },
    })

    try {
      console.log('Creating payment method for validation...')

      // First, submit the elements to validate the payment method
      const { error: submitError } = await elements.submit()
      if (submitError) {
        console.error('Elements submit error:', submitError)

        Sentry.captureMessage('Stripe elements submit error', {
          level: 'error',
          tags: {
            error_type: 'payment_elements_submit_error',
            user_email: formData.email,
          },
          extra: {
            error_message: submitError.message,
            error_code: submitError.code,
            error_type: submitError.type,
            step: 'payment_validation',
          },
        })

        setPaymentError(
          submitError.message || 'Please fill out payment details correctly',
        )
        return false
      }

      console.log('Elements submitted successfully, creating payment method...')

      // Create payment method with billing details
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phoneNumber,
          },
        },
      })

      console.log('Payment method creation result:', { error, paymentMethod })

      if (error) {
        console.error('Payment method creation failed:', error)

        Sentry.captureMessage('Stripe payment method creation failed', {
          level: 'error',
          tags: {
            error_type: 'payment_method_creation_failed',
            user_email: formData.email,
          },
          extra: {
            error_message: error.message,
            error_code: error.code,
            error_type: error.type,
            step: 'payment_validation',
          },
        })

        setPaymentError(error.message || 'Payment validation failed')
        return false
      } else if (paymentMethod) {
        console.log('Payment method created successfully:', paymentMethod.id)
        console.log(
          '🔥 About to call onPaymentValidated with:',
          paymentMethod.id,
        )

        Sentry.addBreadcrumb({
          message: 'Payment method created successfully',
          category: 'registration',
          level: 'info',
          data: {
            email: formData.email,
            payment_method_id: paymentMethod.id,
          },
        })

        onPaymentValidated(paymentMethod.id)
        console.log('✅ onPaymentValidated called successfully')
        return paymentMethod.id
      } else {
        console.error('No payment method returned')

        Sentry.captureMessage('No payment method returned from Stripe', {
          level: 'error',
          tags: {
            error_type: 'payment_method_missing',
            user_email: formData.email,
          },
          extra: {
            step: 'payment_validation',
          },
        })

        setPaymentError('Payment method validation failed')
        return false
      }
    } catch (err) {
      console.error('Payment validation error:', err)

      Sentry.captureException(err, {
        tags: {
          error_type: 'payment_validation_exception',
          user_email: formData.email,
        },
        extra: {
          step: 'payment_validation',
        },
      })

      setPaymentError('Failed to validate payment method')
      return false
    }
  }, [
    stripe,
    elements,
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.phoneNumber,
    onPaymentValidated,
  ])

  // Notify parent when form readiness changes
  React.useEffect(() => {
    const isReady = Boolean(stripe && elements)
    onFormReadyChange(isReady, handlePaymentValidation)
  }, [stripe, elements, handlePaymentValidation, onFormReadyChange])

  return (
    <div className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-lg">
        <p className="text-sm text-gray-600 mb-4">
          Please provide your payment method. You will not be charged during
          registration.
        </p>
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
        {paymentError && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
            <i className="fa-solid fa-exclamation-triangle mr-2" />
            {paymentError}
          </div>
        )}
      </div>

      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-700">
          <i className="fa-solid fa-info-circle mr-2" />
          Fill out your payment details above, then click "Validate Payment &
          Continue" to proceed.
        </p>
      </div>
    </div>
  )
}

const REGISTER_STORAGE_KEY = 'daydaco_register_form'

function Register() {
  // Load saved step from localStorage, default to 1
  const getSavedStep = () => {
    try {
      const saved = localStorage.getItem(REGISTER_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        return parsed.step || 1
      }
    } catch {
      // Ignore parse errors
    }
    return 1
  }

  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [promoCode, setPromoCode] = useState<string>('')
  const [teamInvite, setTeamInvite] = useState<TeamInvite | null>(null)
  const [isTeamInvite, setIsTeamInvite] = useState<boolean>(false)
  const [step, setStep] = useState(getSavedStep)
  const [successModal, setSuccessModal] = useState<boolean>(false)
  const [rejectionModal, setRejectionModal] = useState<boolean>(false)
  const [verifyEmailModal, setVerifyEmailModal] = useState<boolean>(false)
  const [registrationEmail, setRegistrationEmail] = useState<string>('')
  const [paymentMethodId, setPaymentMethodId] = useState<string>('')
  const [isPaymentFormReady, setIsPaymentFormReady] = useState<boolean>(false)
  const [paymentValidationFunction, setPaymentValidationFunction] = useState<
    (() => Promise<string | false>) | null
  >(null)
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState<boolean>(false)

  const navigate = useNavigate()
  const location = useLocation()
  const { register, fetchUser } = useUser()
  const termsContainerRef = useRef<HTMLDivElement>(null)

  // Track component mount
  useEffect(() => {
    Sentry.addBreadcrumb({
      message: 'Register component mounted',
      category: 'registration',
      level: 'info',
      data: {
        user_agent: navigator.userAgent,
      },
    })
  }, [])

  // Load saved form data from localStorage
  const getSavedFormData = (): Partial<RegisterFormData> => {
    try {
      const saved = localStorage.getItem(REGISTER_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        return parsed.formData || {}
      }
    } catch {
      // Ignore parse errors
    }
    return {}
  }

  const savedFormData = getSavedFormData()

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(createRegisterSchema(isTeamInvite)),
    defaultValues: {
      email: savedFormData.email || '',
      password: savedFormData.password || '',
      firstName: savedFormData.firstName || '',
      lastName: savedFormData.lastName || '',
      phoneNumber: savedFormData.phoneNumber || '',
      stakeholderTypes: savedFormData.stakeholderTypes || [],
      companyName: savedFormData.companyName || '',
      corporateAddress: savedFormData.corporateAddress || '',
      companyType: savedFormData.companyType || CompanyType.INDIVIDUAL,
      ccn: savedFormData.ccn || '',
      eid: savedFormData.eid || '',
      licenseNumber: savedFormData.licenseNumber || '',
      permitNumber: savedFormData.permitNumber || '',
      zoningNumber: savedFormData.zoningNumber || '',
    },
  })

  // Save form data and step to localStorage when they change
  useEffect(() => {
    const subscription = form.watch((formData) => {
      try {
        const dataToSave = {
          step,
          formData,
        }
        localStorage.setItem(REGISTER_STORAGE_KEY, JSON.stringify(dataToSave))
      } catch {
        // Ignore storage errors
      }
    })
    return () => subscription.unsubscribe()
  }, [form, step])

  // Save step changes to localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REGISTER_STORAGE_KEY)
      const existing = saved ? JSON.parse(saved) : {}
      localStorage.setItem(
        REGISTER_STORAGE_KEY,
        JSON.stringify({ ...existing, step }),
      )
    } catch {
      // Ignore storage errors
    }
  }, [step])

  // Helper to clear localStorage on successful registration
  const clearRegistrationStorage = useCallback(() => {
    try {
      localStorage.removeItem(REGISTER_STORAGE_KEY)
    } catch {
      // Ignore storage errors
    }
  }, [])

  const scrollUp = () => {
    if (termsContainerRef.current) {
      termsContainerRef.current.scrollBy({ top: -50, behavior: 'smooth' })
    }
  }

  const scrollDown = () => {
    if (termsContainerRef.current) {
      termsContainerRef.current.scrollBy({ top: 50, behavior: 'smooth' })
    }
  }

  const handleScroll = () => {
    if (termsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        termsContainerRef.current
      // Check if user has scrolled to within 10px of the bottom
      const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10
      setHasScrolledToBottom(isScrolledToBottom)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const code = params.get('promo_code')

    // Track registration flow start
    Sentry.addBreadcrumb({
      message: 'Registration flow started',
      category: 'registration',
      level: 'info',
      data: {
        has_promo_code: !!code,
        promo_code: code || 'none',
        url: location.pathname + location.search,
      },
    })

    if (code) {
      setPromoCode(code)
      // Check if it's a team invite promo code (starts with 'team')
      if (code.startsWith('team')) {
        setIsTeamInvite(true)
        // Try to fetch team invite details
        fetchTeamInviteDetails(code)
      }
    }
  }, [location])

  const fetchTeamInviteDetails = async (code: string) => {
    Sentry.addBreadcrumb({
      message: 'Fetching team invite details',
      category: 'registration',
      level: 'info',
      data: {
        promo_code: code,
      },
    })

    try {
      const invite = await userService.getTeamInviteByPromoCode(code)
      setTeamInvite(invite)
      // Auto-populate form fields
      form.setValue('email', invite.email)
      // Note: stakeholder_types are now inherited from the inviter's company,
      // and user_type is handled during registration completion

      Sentry.addBreadcrumb({
        message: 'Team invite details fetched successfully',
        category: 'registration',
        level: 'info',
        data: {
          email: invite.email,
          user_type: invite.user_type,
        },
      })
    } catch (error) {
      console.error('Failed to fetch team invite details:', error)

      Sentry.captureException(error, {
        tags: {
          error_type: 'team_invite_fetch_failed',
          promo_code: code,
        },
        extra: {
          step: 'team_invite_fetch',
        },
      })

      // If we can't fetch invite details, still treat as team invite to skip steps
    }
  }

  const onSubmit = async (data: RegisterFormData) => {
    // User already created in previous step, now initiate email verification after terms agreement
    setError('')
    setLoading(true)

    Sentry.addBreadcrumb({
      message: 'Email verification process started',
      category: 'registration',
      level: 'info',
      data: {
        email: data.email,
        step: 'email_verification',
      },
    })

    try {
      // Verify we have the session token from user registration
      const sessionToken = window.localStorage.getItem('daydaco.authkey')
      if (!sessionToken) {
        console.error(
          '❌ No session token found - user registration may have failed',
        )

        Sentry.captureMessage('No session token found for email verification', {
          level: 'error',
          tags: {
            error_type: 'email_verification_no_session_token',
            user_email: data.email,
          },
          extra: {
            step: 'email_verification',
            current_step: step,
          },
        })

        setError('Registration incomplete. Please try registering again.')
        return
      }

      console.log(`✓ Found session token, sending OTP email to: ${data.email}`)

      // First, send the OTP email
      const result = await sendEmailOtpService(data.email)

      if (result.error) {
        const errorData = result.error.response?.data
        const errorMessage =
          errorData?.detail ||
          errorData?.message ||
          'Failed to send verification email. Please try again.'
        console.error('❌ Failed to send OTP email:', errorData)

        Sentry.captureMessage('Failed to send OTP email', {
          level: 'error',
          tags: {
            error_type: 'otp_email_send_failed',
            user_email: data.email,
          },
          extra: {
            error_data: errorData,
            error_message: errorMessage,
            step: 'email_verification',
          },
        })

        setError(errorMessage)
        return
      }

      console.log('✓ OTP email sent successfully')

      Sentry.addBreadcrumb({
        message: 'OTP email sent successfully',
        category: 'registration',
        level: 'info',
        data: {
          email: data.email,
        },
      })

      // If email sending successful, show verification modal
      setRegistrationEmail(data.email)
      setVerifyEmailModal(true)
    } catch (err) {
      console.error('❌ Error sending OTP email:', err)

      Sentry.captureException(err, {
        tags: {
          error_type: 'otp_email_send_exception',
          user_email: data.email,
        },
        extra: {
          step: 'email_verification',
          current_step: step,
        },
      })

      setError('Failed to send verification email. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const createUserAccount = async (
    formData: RegisterFormData,
    overridePaymentMethodId?: string,
  ) => {
    const finalPaymentMethodId = overridePaymentMethodId || paymentMethodId
    console.log(
      '🚀 createUserAccount called with paymentMethodId:',
      finalPaymentMethodId,
    )
    setError('')
    setLoading(true)

    // Add Sentry breadcrumb for registration attempt
    Sentry.addBreadcrumb({
      message: 'User registration started',
      category: 'registration',
      level: 'info',
      data: {
        email: formData.email,
        stakeholder_types: formData.stakeholderTypes,
        company_name: formData.companyName,
        is_team_invite: isTeamInvite,
        promo_code: promoCode || 'none',
        step: 'create_account',
      },
    })

    try {
      const res = await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phoneNumber,
        stakeholder_types: formData.stakeholderTypes || [],
        company_name: formData.companyName || '',
        company_address: formData.corporateAddress || '',
        company_type: formData.companyType || CompanyType.INDIVIDUAL,
        ccn: formData.ccn || '',
        eid: formData.eid || '',
        license_number: formData.licenseNumber || '',
        permit_number: formData.permitNumber || '',
        zoning_number: formData.zoningNumber || '',
        promo_code: promoCode,
        payment_method_id: isTeamInvite ? '' : finalPaymentMethodId,
      })

      if (res.error) {
        // Access status from the axios response (not from our typed structure)
        const statusCode = (res.error as ExtendedErrorResponse).response?.status
        const errorResponse = res.error.response?.data

        if (statusCode === 401) {
          // User created successfully but needs email verification
          console.log('User created, needs email verification:', res.error)

          Sentry.addBreadcrumb({
            message: 'User created, email verification required',
            category: 'registration',
            level: 'info',
            data: {
              email: formData.email,
              status_code: statusCode,
              has_error_response: !!res.error.response,
            },
          })

          // Capture session token from the 401 response for email verification
          try {
            if (
              errorResponse &&
              typeof errorResponse === 'object' &&
              'meta' in errorResponse
            ) {
              const metaData = (
                errorResponse as { meta?: { session_token?: string } }
              ).meta
              if (metaData?.session_token) {
                // Store the session token from the 401 response
                window.localStorage.setItem(
                  'daydaco.authkey',
                  metaData.session_token,
                )
                console.log(
                  `✓ User created successfully - Stored session token for email verification: ${metaData.session_token.substring(0, 10)}...`,
                )
              } else {
                console.warn('⚠️ No session token found in 401 response meta')
                Sentry.captureMessage(
                  'No session token found in 401 response meta',
                  {
                    level: 'warning',
                    tags: {
                      error_type: 'registration_session_token_missing',
                      user_email: formData.email,
                      status_code: statusCode,
                    },
                    extra: { errorResponse },
                  },
                )
              }
            } else {
              console.warn('⚠️ No meta object found in 401 response')
              Sentry.captureMessage('No meta object found in 401 response', {
                level: 'warning',
                tags: {
                  error_type: 'registration_meta_missing',
                  user_email: formData.email,
                  status_code: statusCode,
                },
                extra: { errorResponse },
              })
            }
          } catch (e) {
            console.error(
              '❌ Could not extract session token from 401 response:',
              e,
            )
            Sentry.captureException(e, {
              tags: {
                error_type: 'registration_session_extraction_failed',
                user_email: formData.email,
                status_code: statusCode,
              },
              extra: {
                registration_response: errorResponse,
                step: 'session_token_extraction',
              },
            })
          }

          // User created successfully, proceed to next step
          return true
        } else {
          // Handle other error types (400, 409, 500, etc.)
          console.error(
            '❌ Registration failed with status:',
            statusCode,
            res.error,
          )

          // Extract error message for user display from the errors array
          const firstError = errorResponse?.errors?.[0]
          let errorMessage = 'Registration failed. Please try again.'
          if (firstError?.message) {
            const fieldName = firstError.param
              ? `${firstError.param.charAt(0).toUpperCase()}${firstError.param.slice(1).replace(/_/g, ' ')}`
              : null
            errorMessage = fieldName
              ? `${fieldName}: ${firstError.message}`
              : firstError.message
          }

          // Log detailed error information to Sentry
          Sentry.captureMessage('Registration failed with HTTP error', {
            level: 'error',
            tags: {
              error_type: 'registration_http_error',
              user_email: formData.email,
              status_code: statusCode,
              is_team_invite: isTeamInvite,
            },
            extra: {
              error_response: errorResponse,
              status_code: statusCode,
              errors: errorResponse?.errors || [],
              form_data: {
                email: formData.email,
                stakeholder_types: formData.stakeholderTypes,
                company_name: formData.companyName,
                company_type: formData.companyType,
                has_payment_method: !!paymentMethodId,
                promo_code: promoCode || 'none',
              },
              step: 'create_account',
              current_step: step,
              full_error: res.error,
            },
          })

          setError(errorMessage)
          return false
        }
      } else {
        // Registration successful without verification needed
        Sentry.addBreadcrumb({
          message: 'User registration completed successfully',
          category: 'registration',
          level: 'info',
          data: {
            email: formData.email,
            verification_required: false,
          },
        })
        return true
      }
    } catch (error) {
      console.error('Registration error:', error)

      // Check if this is a timeout error
      const errorObj = error as { code?: string; message?: string }
      const isTimeoutError = Boolean(
        error &&
          typeof error === 'object' &&
          (errorObj.code === 'ECONNABORTED' ||
            errorObj.message?.includes('timeout')),
      )

      // Capture detailed error information for Sentry
      Sentry.captureException(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: {
            error_type: isTimeoutError
              ? 'registration_timeout'
              : 'registration_failed',
            user_email: formData.email,
            is_team_invite: isTeamInvite,
            is_timeout: isTimeoutError,
          },
          extra: {
            form_data: {
              email: formData.email,
              stakeholder_types: formData.stakeholderTypes,
              company_name: formData.companyName,
              company_type: formData.companyType,
              has_payment_method: !!paymentMethodId,
              promo_code: promoCode || 'none',
            },
            step: 'create_account',
            current_step: step,
            error_code: errorObj.code || 'unknown',
            error_message: errorObj.message || 'unknown',
            request_duration: isTeamInvite
              ? '10+ seconds (timeout)'
              : 'unknown',
          },
        },
      )

      if (isTimeoutError && isTeamInvite) {
        setError(
          'Team registration is taking longer than expected. The registration may have succeeded - please check your email or try logging in.',
        )
      } else {
        setError('Registration failed. Please try again.')
      }
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleNext = async () => {
    let isValid = false
    const formData = form.getValues()

    if (step === 1) {
      isValid = await form.trigger([
        'firstName',
        'lastName',
        'email',
        'phoneNumber',
        'password',
        'stakeholderTypes',
      ])
      console.log('Step 1 validation result:', isValid)

      if (!isValid) {
        // Capture validation errors for step 1
        const errors = form.formState.errors
        Sentry.captureMessage('Step 1 form validation failed', {
          level: 'info',
          tags: {
            error_type: 'form_validation_failed',
            step: 1,
            user_email: formData.email || 'unknown',
          },
          extra: {
            validation_errors: Object.keys(errors),
            is_team_invite: isTeamInvite,
          },
        })
      }

      if (isValid && isTeamInvite) {
        // Create user account for promo code flow before terms agreement
        setError('') // Clear any previous errors
        console.log(
          '🔄 Starting team invite registration (this may take up to 10 seconds)...',
        )

        const success = await createUserAccount(formData, '')
        if (success) {
          setStep(4) // Go to terms agreement
        }
        return
      }
    } else if (step === 2) {
      // Get current company type to determine which fields to validate
      const companyType = form.getValues('companyType')
      const fieldsToValidate = [
        'companyName',
        'corporateAddress',
        'companyType',
      ]

      // Add conditional fields based on company type
      if (
        companyType === CompanyType.C_CORP ||
        companyType === CompanyType.S_CORP
      ) {
        fieldsToValidate.push('ccn')
      } else if (
        companyType === CompanyType.LLC ||
        companyType === CompanyType.PARTNERSHIP
      ) {
        fieldsToValidate.push('eid')
      } else if (companyType === CompanyType.INDIVIDUAL) {
        fieldsToValidate.push('licenseNumber', 'permitNumber', 'zoningNumber')
      }

      isValid = await form.trigger(
        fieldsToValidate as (keyof RegisterFormData)[],
      )

      if (!isValid) {
        // Capture validation errors for step 2
        const errors = form.formState.errors
        Sentry.captureMessage('Step 2 form validation failed', {
          level: 'info',
          tags: {
            error_type: 'form_validation_failed',
            step: 2,
            user_email: formData.email,
            company_type: companyType,
          },
          extra: {
            validation_errors: Object.keys(errors),
            fields_to_validate: fieldsToValidate,
            company_type: companyType,
          },
        })
      }
    } else if (step === 3) {
      // Combined payment validation and user account creation
      if (!paymentValidationFunction) {
        const errorMsg =
          'Payment form not ready. Please wait a moment and try again.'

        Sentry.captureMessage('Payment validation function not ready', {
          level: 'error',
          tags: {
            error_type: 'payment_function_not_ready',
            user_email: formData.email,
          },
          extra: {
            step: 3,
            is_payment_form_ready: isPaymentFormReady,
            has_payment_method_id: !!paymentMethodId,
          },
        })

        setError(errorMsg)
        return
      }

      setLoading(true)
      setError('')

      Sentry.addBreadcrumb({
        message: 'Payment validation and account creation step started',
        category: 'registration',
        level: 'info',
        data: {
          step: 3,
          email: formData.email,
        },
      })

      try {
        // First validate the payment method
        console.log(
          '🔍 About to call paymentValidationFunction, current paymentMethodId:',
          paymentMethodId,
        )
        console.log(
          '🔍 paymentValidationFunction exists:',
          !!paymentValidationFunction,
        )
        if (!paymentValidationFunction) {
          console.error('❌ paymentValidationFunction is null!')
          setError('Payment validation function not ready')
          return
        }
        const paymentResult = await paymentValidationFunction()
        console.log(
          '💳 Payment validation result:',
          paymentResult,
          'Updated paymentMethodId:',
          paymentMethodId,
        )
        if (!paymentResult) {
          // Error is already set by the payment validation function
          return
        }

        // Update paymentMethodId with the returned value
        const validatedPaymentMethodId =
          typeof paymentResult === 'string' ? paymentResult : paymentMethodId
        console.log(
          '🎯 Using payment method ID for registration:',
          validatedPaymentMethodId,
        )

        // If payment validation succeeds, create user account with the validated payment method ID
        const success = await createUserAccount(
          formData,
          validatedPaymentMethodId,
        )
        if (success) {
          setStep(4) // Go to terms agreement
        }
      } catch (err) {
        console.error('Payment validation error:', err)

        Sentry.captureException(err, {
          tags: {
            error_type: 'payment_validation_step_failed',
            user_email: formData.email,
          },
          extra: {
            step: 3,
            has_payment_method_id: !!paymentMethodId,
          },
        })

        setError('Failed to validate payment method. Please try again.')
      } finally {
        setLoading(false)
      }
      return
    } else if (step === 4) {
      isValid = true // No validation needed for terms step
    }

    if (isValid) {
      // Track successful step progression
      Sentry.addBreadcrumb({
        message: `User progressed from step ${step} to step ${step + 1}`,
        category: 'registration',
        level: 'info',
        data: {
          from_step: step,
          to_step: step + 1,
          email: formData.email,
          is_team_invite: isTeamInvite,
        },
      })

      setStep((prev) => prev + 1)
    }
  }

  const handlePaymentValidated = useCallback(
    (validatedPaymentMethodId: string) => {
      console.log(
        '🎯 handlePaymentValidated called with ID:',
        validatedPaymentMethodId,
      )
      setPaymentMethodId(validatedPaymentMethodId)
      setError('') // Clear any previous errors
    },
    [],
  )

  const handlePaymentFormReadyChange = useCallback(
    (isReady: boolean, validationFunction: () => Promise<string | false>) => {
      setIsPaymentFormReady(isReady)
      setPaymentValidationFunction(() => validationFunction)
    },
    [],
  )

  const handleEmailVerificationSuccess = async () => {
    // After successful email verification, fetch user data to update context
    setVerifyEmailModal(false)

    Sentry.addBreadcrumb({
      message: 'Email verification successful, fetching user data',
      category: 'registration',
      level: 'info',
      data: {
        email: registrationEmail,
      },
    })

    try {
      console.log('✓ Email verified successfully, fetching user data...')
      const userData = await fetchUser()

      if (userData?.id && userData?.email) {
        console.log(
          `✓ User data fetched successfully for ${userData.email} (ID: ${userData.id}), showing success modal`,
        )

        // Set user context for Sentry
        Sentry.setUser({
          id: userData.id,
          email: userData.email,
          username: `${userData.first_name} ${userData.last_name}`,
        })

        Sentry.addBreadcrumb({
          message: 'Registration completed successfully',
          category: 'registration',
          level: 'info',
          data: {
            user_id: userData.id,
            email: userData.email,
          },
        })

        clearRegistrationStorage()
        setSuccessModal(true)
      } else {
        console.error(
          '❌ Failed to fetch complete user data after verification:',
          userData,
        )

        Sentry.captureMessage(
          'Failed to fetch complete user data after email verification',
          {
            level: 'error',
            tags: {
              error_type: 'user_data_fetch_incomplete',
              user_email: registrationEmail,
            },
            extra: {
              user_data: userData,
              step: 'post_email_verification',
            },
          },
        )

        setError(
          'Email verified but failed to load complete user data. Please try logging in.',
        )
      }
    } catch (err) {
      console.error('❌ Error fetching user data after verification:', err)

      Sentry.captureException(err, {
        tags: {
          error_type: 'user_data_fetch_failed',
          user_email: registrationEmail,
        },
        extra: {
          step: 'post_email_verification',
        },
      })

      setError(
        'Email verified but failed to load user data. Please try logging in.',
      )
    }
  }

  const handleEmailVerificationClose = () => {
    setVerifyEmailModal(false)

    Sentry.addBreadcrumb({
      message: 'User closed email verification modal',
      category: 'registration',
      level: 'info',
      data: {
        email: registrationEmail,
        step: 'email_verification_cancelled',
      },
    })

    // Don't show success modal if user cancels verification
    // They can complete verification later by logging in
  }

  const handleAcceptTerms = () => {
    // Show confirmation popup before proceeding with terms acceptance
    Sentry.addBreadcrumb({
      message: 'User clicked accept terms button',
      category: 'registration',
      level: 'info',
      data: {
        email: form.getValues('email'),
        step: 'terms_acceptance_initiated',
      },
    })

    setConfirmationModal(true)
  }

  const handleConfirmAcceptance = () => {
    // Close confirmation modal and proceed with form submission
    setConfirmationModal(false)

    Sentry.addBreadcrumb({
      message: 'User confirmed terms acceptance',
      category: 'registration',
      level: 'info',
      data: {
        email: form.getValues('email'),
        step: 'terms_acceptance_confirmed',
      },
    })

    // Trigger the actual form submission to start OTP flow
    form.handleSubmit(onSubmit)()
  }

  return (
    <div className="h-screen flex flex-col md:flex-row relative">
      {/* Fixed left section with background image */}
      <div
        className="hidden md:block md:w-[40%] bg-cover bg-center relative"
        style={{ backgroundImage: "url('/signup.webp')" }}
      >
        {/* Logo stretched across left section */}
        <img
          src="/dd_logo_white.png"
          alt="DayDaCo"
          className="absolute top-0 left-0 right-0 z-10 object-contain"
        />
      </div>

      {/* Scrollable right section */}
      <div className="flex-1 md:w-[60%] overflow-y-auto">
        {/* Login link in top right corner of scrollable section */}
        <div className="flex justify-end p-4">
          <p className="text-sm">
            Already have an account?{' '}
            <a
              href="/login"
              className="font-semibold text-blue-600 hover:underline"
            >
              Login here
            </a>
          </p>
        </div>
        <div
          className={`flex flex-col items-center px-8 ${isTeamInvite ? 'pb-8' : 'pb-0'} ${step === 4 ? 'min-h-0' : 'min-h-[calc(100vh-80px)] justify-center'}`}
        >
          <div className="w-full max-w-4xl">
            <h1 className="text-3xl font-bold mb-4 text-center">
              Create account
            </h1>
            <div className="mb-8 max-w-3xl mx-auto">
              <Stepper
                currentStep={isTeamInvite ? (step === 1 ? 1 : 2) : step}
                steps={
                  isTeamInvite
                    ? ['Your info', 'Terms of Service']
                    : [
                        'Your info',
                        'Company info',
                        'Credit card info',
                        'Terms of Service',
                      ]
                }
              />
            </div>

            {/* Content container with narrower width for steps 1-3 */}
            <div
              className={`w-full mx-auto ${step === 4 ? 'max-w-4xl' : 'max-w-md'}`}
            >
              {isTeamInvite && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <i className="fa-solid fa-users text-blue-600 mr-2" />
                    <span className="text-sm text-blue-700 font-medium">
                      {teamInvite?.company?.name
                        ? `Joining ${teamInvite.company.name}`
                        : 'Team Invitation Registration'}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    You're joining a team! Company and payment information will
                    be handled by your team administrator.
                  </p>
                </div>
              )}

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {step === 1 && (
                    <>
                      <h2 className="text-lg font-semibold">
                        Enter your contact information
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>First Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="John"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Last Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Doe"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Email{' '}
                              {isTeamInvite && (
                                <span className="text-xs text-blue-600">
                                  (from team invitation)
                                </span>
                              )}
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="name@example.com"
                                autoComplete="off"
                                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400 ${isTeamInvite ? 'bg-blue-50' : 'bg-white'}`}
                                disabled={isTeamInvite}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                              <PhoneInput
                                placeholder="Enter phone number"
                                value={field.value}
                                onChange={field.onChange}
                                defaultCountry="US"
                                className="w-full [&>.PhoneInputInput]:bg-white"
                                style={{
                                  '--PhoneInput-color--focus': '#2563eb',
                                  '--PhoneInputInternationalIconPhone-opacity':
                                    '0.8',
                                  '--PhoneInputInternationalIconGlobe-opacity':
                                    '0.65',
                                }}
                                numberInputProps={{
                                  className:
                                    'w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <Input
                                type="password"
                                placeholder="Enter your password"
                                autoComplete="new-password"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {!isTeamInvite && (
                        <FormField
                          control={form.control}
                          name="stakeholderTypes"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company Types</FormLabel>
                              <FormControl>
                                <MultiSelect
                                  placeholder="Select company types"
                                  options={STAKEHOLDER_OPTIONS}
                                  value={STAKEHOLDER_OPTIONS.filter((option) =>
                                    (field.value || []).includes(
                                      option.value as StakeholderType,
                                    ),
                                  )}
                                  onChange={(selected) => {
                                    const selectedTypes = (
                                      (selected as {
                                        value: StakeholderType
                                      }[]) || []
                                    ).map((o) => o.value)
                                    field.onChange(selectedTypes)
                                  }}
                                  isMulti={true}
                                  customControlStyles={{
                                    borderRadius: '8px',
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {isTeamInvite && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <i className="fa-solid fa-info-circle text-blue-500" />
                            <span className="text-sm text-blue-700 font-medium">
                              Team Invitation
                            </span>
                          </div>
                          <p className="text-sm text-blue-600 mt-1">
                            Your company access will be inherited from your team
                            invitation.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <h2 className="text-lg font-semibold">
                        Enter your company information
                      </h2>

                      <FormField
                        control={form.control}
                        name="companyType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Type</FormLabel>
                            <FormControl>
                              <Select
                                placeholder="Select company type"
                                options={COMPANY_TYPE_OPTIONS}
                                value={
                                  COMPANY_TYPE_OPTIONS.find(
                                    (option) => option.value === field.value,
                                  ) || undefined
                                }
                                onChange={(option) =>
                                  field.onChange(
                                    option?.value || CompanyType.INDIVIDUAL,
                                  )
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Acme Corp"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="corporateAddress"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Corporate Address</FormLabel>
                            <FormControl>
                              <GooglePlacesSelect
                                placeholder="123 Business St, City, State 12345"
                                value={
                                  field.value
                                    ? { label: field.value, value: field.value }
                                    : null
                                }
                                onChange={(option) =>
                                  field.onChange(option?.value || '')
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Conditional fields based on company type */}
                      {(form.watch('companyType') === CompanyType.C_CORP ||
                        form.watch('companyType') === CompanyType.S_CORP) && (
                        <FormField
                          control={form.control}
                          name="ccn"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                CCN (Corporate Credit Number)
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter CCN"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {(form.watch('companyType') === CompanyType.LLC ||
                        form.watch('companyType') ===
                          CompanyType.PARTNERSHIP) && (
                        <FormField
                          control={form.control}
                          name="eid"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                EID (Employer Identification Number)
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter EID"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {form.watch('companyType') === CompanyType.INDIVIDUAL && (
                        <>
                          <FormField
                            control={form.control}
                            name="licenseNumber"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>License Number</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter license number"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="permitNumber"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Permit Number</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter permit number"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="zoningNumber"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Zoning Number</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter zoning number"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-gray-400"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </>
                      )}
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <h2 className="text-lg font-semibold">
                        Payment Information
                      </h2>
                      <Elements
                        stripe={stripePromise}
                        options={{
                          mode: 'setup',
                          currency: 'usd',
                          paymentMethodCreation: 'manual',
                        }}
                      >
                        <PaymentValidationForm
                          formData={form.getValues()}
                          onPaymentValidated={handlePaymentValidated}
                          onFormReadyChange={handlePaymentFormReadyChange}
                        />
                      </Elements>
                    </>
                  )}

                  {step === 4 && (
                    <>
                      <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold mb-2">
                          Terms of Service Agreement
                        </h2>
                      </div>

                      {/* Document action buttons */}
                      <div className="flex gap-2 -mb-4">
                        <button
                          type="button"
                          onClick={() => {
                            /* TODO: Implement print functionality */
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded border transition-colors duration-200"
                          aria-label="Print terms"
                          title="Print Terms"
                        >
                          <i className="fa-solid fa-print text-blue-600 text-xs" />
                          <span className="text-xs text-blue-700 font-medium">
                            Print
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            /* TODO: Implement save functionality */
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 rounded border transition-colors duration-200"
                          aria-label="Save terms"
                          title="Save Terms"
                        >
                          <i className="fa-solid fa-floppy-disk text-green-600 text-xs" />
                          <span className="text-xs text-green-700 font-medium">
                            Save
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            /* TODO: Implement download functionality */
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-purple-100 hover:bg-purple-200 rounded border transition-colors duration-200"
                          aria-label="Download terms"
                          title="Download Terms"
                        >
                          <i className="fa-solid fa-download text-purple-600 text-xs" />
                          <span className="text-xs text-purple-700 font-medium">
                            Download
                          </span>
                        </button>
                      </div>

                      <div className="relative flex items-center">
                        <div
                          ref={termsContainerRef}
                          className="flex-1 border rounded-lg p-4 bg-gray-50 max-h-[28rem] overflow-y-auto space-y-4 mr-2"
                          onScroll={handleScroll}
                        >
                          <div className="space-y-4 text-xs leading-relaxed">
                            <div>
                              <h3 className="font-bold text-base mb-2">
                                Terms of Service Agreement
                              </h3>
                              <p className="font-semibold">
                                EFFECTIVE DATE: May [...], 2025
                              </p>
                            </div>

                            <div>
                              <h4 className="font-semibold mb-2">
                                Introduction
                              </h4>
                              <p>
                                The Terms of Service set out the terms,
                                conditions, duties, responsibilities, and
                                obligations upon which DayDaCo, Inc. offers you
                                to apply for and register a user account. You
                                (The Registered User) may access and use the
                                Digital Platform Ecosystem based on your
                                acceptance of the Terms of Service.
                              </p>
                            </div>

                            <div>
                              <h4 className="font-semibold mb-2">
                                About DayDaCo - Who We Are
                              </h4>
                              <p>
                                DayDaCo, Inc. built a novel multi-layered
                                software system and mobile phone application.
                                This computer network is called "Digital
                                Platform Ecosystem". The Digital Platform
                                Ecosystem provides a technological
                                infrastructure for a digital Dump Truck
                                marketplace.
                              </p>
                            </div>

                            <div>
                              <h4 className="font-semibold mb-2">
                                What DayDaCo Is Not
                              </h4>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  DayDaCo does not collect, facilitate, nor
                                  transmit payments between and among the Users
                                  for any transportation services
                                </li>
                                <li>
                                  DayDaCo is not a party to, broker, middleman,
                                  or intermediary to any transaction between and
                                  among You and one or more Registered Users
                                </li>
                                <li>
                                  DayDaCo, Inc. is not a Broker and does not
                                  advertise, market, offer, or provide any Dump
                                  Truck trucking or brokerage services
                                </li>
                                <li>
                                  DayDaCo, Inc. is not a public or private
                                  common carrier
                                </li>
                                <li>
                                  DayDaCo, Inc. does not organize, facilitate,
                                  provide, or arrange for transportation
                                  services in or to the Digital Platform
                                  Ecosystem of any material
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-semibold mb-2">
                                The User Is Not Involved in Interstate Commerce
                              </h4>
                              <h5 className="font-medium mb-1">
                                Interstate Commerce Act
                              </h5>
                              <p className="mb-2">
                                The User acknowledges and agrees that neither
                                the operations of the business of the User nor
                                any of the User's vehicles, services, assets, or
                                properties is subject to any rules or
                                regulations under the Interstate Commerce Act of
                                1887, PL [...], 24 Statute 379 as amended to
                                date, or any rules or regulations under the
                                Motor Carrier Act of 1935, (PL 74-255, 49
                                Statute. 543 as amended to date.
                              </p>
                              <h5 className="font-medium mb-1">
                                Only Intrastate Commerce
                              </h5>
                              <p>
                                The User acknowledges and agrees that User is
                                not engaged in any acts, activities, conducts,
                                exchanges, or transactions involving interstate
                                commerce. The User's transportation services do
                                not involve interstate commerce.
                              </p>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                1. CONTRACTUAL RELATIONSHIP
                              </h4>
                              <p className="mb-2">
                                The following provisions are an agreement
                                between you and DayDaCo, Inc. ("DayDaCo"). These
                                provisions as well as the DayDaCo Privacy
                                Policy, and all other policies and guidelines
                                posted on the DayDaCo homepage, and its webpages
                                (collectively, "Terms of Service") govern your
                                application for and registration of a User
                                Account and govern your access to and use of the
                                Digital Platform Ecosystem.
                              </p>
                              <p className="font-semibold mb-2">
                                PLEASE READ ALL THE PROVISIONS OF THE TERMS OF
                                SERVICE CAREFULLY, AS THEY CONSTITUTE A LEGALLY
                                BINDING AGREEMENT BETWEEN YOU AND DAYDACO.
                              </p>
                              <p>
                                By clicking the "I Agree" button during the
                                Sign-up process, you acknowledge and agree that
                                the access to and use of the DayDaCo Ecosystem
                                will require you to strictly abide at all times
                                by all provisions of this Terms of Service.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                DISPUTE RESOLUTION - IMPORTANT NOTICE
                              </h5>
                              <p className="font-semibold mb-2">
                                PLEASE BE ADVISED THAT IF YOU ACCEPT THE TERMS
                                OF SERVICE YOU AGREE TO ARBITRATE ANY AND ALL
                                DISPUTES, CONTROVERSIES, AND CLAIMS, INCLUDING
                                BUT NOT LIMITED TO, ALL STATUTORY CLAIMS, AND
                                ANY STATE OR FEDERAL CLAIMS.
                              </p>
                              <p className="mb-2">
                                The Terms of Service contains provisions that
                                govern if, when, for how long, and how disputes,
                                controversies, or claims between you and DayDaCo
                                can be brought, administered or resolved,
                                including:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>An arbitration agreement</li>
                                <li>
                                  A waiver of your constitutional right to a
                                  jury trial
                                </li>
                                <li>
                                  A waiver of your right to file a lawsuit in a
                                  court of law
                                </li>
                                <li>A limitation of liability</li>
                                <li>Your indemnity to DayDaCo</li>
                                <li>
                                  A waiver of your right to participate in class
                                  actions
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (a) Addition, Replacement, Change, Revision,
                                Update, Modification or Amendment
                              </h5>
                              <p className="mb-2">
                                DayDaCo may unilaterally, in its sole
                                discretion, add new provisions, and make
                                unilaterally any replacements, additions,
                                changes, revisions, updates, modifications, or
                                amendments to the provisions of the Terms of
                                Service at any time without prior notice or
                                warning to you.
                              </p>
                              <p className="mb-2">
                                All updates are effective immediately and
                                legally binding upon you when DayDaCo posts them
                                on DayDaCo's website at
                                https://www.DayDaCo.com/UserAgreement.
                              </p>
                              <p className="font-semibold">
                                It is your affirmative duty and responsibility
                                to check DayDaCo's website periodically for
                                updates.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (b) Important Policies and Guidelines
                              </h5>
                              <p className="mb-2">
                                The following documents are an integral part of
                                the Terms of Service:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  <strong>Privacy Policy:</strong>{' '}
                                  https://www.DayDaCo.com/privacy/notice
                                </li>
                                <li>
                                  <strong>Cookie Policy:</strong>{' '}
                                  https://www.DayDaCo.com/cookie/notice
                                </li>
                                <li>
                                  <strong>Community Guidelines:</strong>{' '}
                                  https://www.DayDaCo.com/community/guidelines
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (c) Your Business Relationship with DayDaCo
                              </h5>
                              <p>
                                As a Registered User, you acknowledge and agree
                                that you and DayDaCo are in a legal relationship
                                as independent contracting parties only. This is
                                not an employment agreement, and no agent,
                                principal, partnership, or other relationship is
                                created.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (d) Age of Consent
                              </h5>
                              <p>
                                You must be greater than 18 years of age and
                                able to enter into legally binding contracts.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (e) Who DayDaCo Is Not
                              </h5>
                              <p>
                                DayDaCo is not registered and does not offer
                                brokerage or any other similar intermediary
                                services. DayDaCo is not a broker, motor
                                carrier, transportation company, or logistics
                                service provider.
                              </p>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                2. ARBITRATION AGREEMENT
                              </h4>
                              <p className="font-semibold mb-2">
                                PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS
                                YOUR LEGAL RIGHTS, INCLUDING WAIVING YOUR
                                CONSTITUTIONAL RIGHT TO A JURY TRIAL AND YOUR
                                RIGHT TO FILE A LAWSUIT IN A COURT OF LAW.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (a) Agreement to Binding Arbitration
                              </h5>
                              <p className="mb-2">
                                <strong>(1) Covered Disputes:</strong> Any
                                dispute, controversy, or claim arising out of,
                                relating to, or in connection with:
                              </p>
                              <ul className="list-disc ml-5 space-y-1 mb-2">
                                <li>
                                  The application, registration and account
                                  creation process
                                </li>
                                <li>The provisions of the Terms of Service</li>
                                <li>
                                  Your access to or use of the Digital Platform
                                  Ecosystem
                                </li>
                                <li>
                                  Any incident or accident resulting in personal
                                  injury
                                </li>
                                <li>
                                  Your connection or relationship with DayDaCo
                                </li>
                              </ul>
                              <p className="mb-2">
                                <strong>(2) Survival:</strong> This Arbitration
                                Agreement survives after the Terms of Service
                                terminates, expires, or your relationship with
                                DayDaCo ends.
                              </p>
                              <p className="mb-2">
                                <strong>(3) Class Action Waiver:</strong>{' '}
                                Disputes shall be resolved only in individual
                                arbitration. You waive the right to participate
                                in class, collective, or representative actions.
                              </p>
                              <p className="mb-2">
                                <strong>(4) Mass Actions:</strong>
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  Mass Action Waiver applies when 50 or more
                                  similar arbitration demands are filed within
                                  182 days
                                </li>
                                <li>
                                  Special procedures for batching and processing
                                  mass actions
                                </li>
                                <li>
                                  Disputes will be grouped into batches of no
                                  more than 100 demands per batch
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (b) Exceptions to Arbitration
                              </h5>
                              <p className="mb-2">
                                The following claims are NOT subject to
                                arbitration:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>Individual claims in small claims court</li>
                                <li>
                                  Individual claims of sexual assault or sexual
                                  harassment
                                </li>
                                <li>
                                  Injunctive relief for intellectual property
                                  rights
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (c) Rules and Governing Law
                              </h5>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  <strong>California disputes:</strong> ADR
                                  Services, Inc. in accordance with ADR Rules
                                </li>
                                <li>
                                  <strong>Outside California:</strong> Parties
                                  must meet and confer to select neutral
                                  arbitration provider
                                </li>
                                <li>
                                  Federal Arbitration Act governs (except
                                  Section 1)
                                </li>
                                <li>
                                  Confidentiality required for all proceedings
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (d) Process
                              </h5>
                              <p className="mb-2">
                                <strong>Pre-Arbitration:</strong> Good-faith
                                informal dispute resolution conference required
                                within 60 days.
                              </p>
                              <p>
                                <strong>Initiating Arbitration:</strong> Written
                                demand required after informal process
                                completion.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (e) Location
                              </h5>
                              <p>
                                Arbitration conducted in the county where you
                                reside.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (f) Arbitrator's Decision
                              </h5>
                              <p>
                                Final and binding on all parties. May award
                                declaratory or injunctive relief only to
                                individual claimant.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">(g) Fees</h5>
                              <p>
                                Filing, administrative, and arbitrator fees as
                                set forth in applicable arbitration provider's
                                rules. Fee waivers available for those with
                                gross monthly income less than 300% of federal
                                poverty guidelines.
                              </p>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                3. THE DIGITAL PLATFORM ECOSYSTEM
                              </h4>
                              <p className="mb-2">
                                DayDaCo built a technological architecture that
                                hosts a digital platform ecosystem accessed
                                through mobile and web-based applications.
                              </p>
                              <p className="font-semibold mb-2">KEY POINTS:</p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  DayDaCo is NOT a transportation carrier,
                                  broker, or logistics provider
                                </li>
                                <li>
                                  Access is limited to Registered Users only
                                </li>
                                <li>
                                  DayDaCo may unilaterally restrict or terminate
                                  access at any time
                                </li>
                                <li>Users are independent contractors</li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                Limited License
                              </h5>
                              <p>
                                Subject to compliance with Terms of Service,
                                DayDaCo grants you a limited, personal,
                                non-commercial, non-exclusive, revocable license
                                to access and use the Digital Platform
                                Ecosystem.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                User Restrictions
                              </h5>
                              <p className="mb-2">You shall not:</p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>Remove proprietary notices</li>
                                <li>Create competing services</li>
                                <li>Copy, reproduce, or distribute content</li>
                                <li>Reverse engineer the platform</li>
                                <li>Attempt unauthorized access</li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                4. ELIGIBILITY AND USER ACCOUNT
                              </h4>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                Eligibility for Account Setup
                              </h5>
                              <p className="mb-2">
                                To be eligible, you must be a Company that has
                                been:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  Duly organized and validly existing as a
                                  corporation
                                </li>
                                <li>In good standing under state laws</li>
                                <li>Incorporated in one of the 50 States</li>
                                <li>
                                  Qualified, licensed, and permitted to do
                                  business
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                Registration of User Account
                              </h5>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  Complete verification procedures required
                                </li>
                                <li>
                                  Must use account only for yourself unless
                                  written approval obtained
                                </li>
                                <li>
                                  Responsible for all activity under your
                                  account
                                </li>
                                <li>
                                  Must provide accurate, current information
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                User Requirements and Conduct
                              </h5>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>Must abide by Community Guidelines</li>
                                <li>Comply with all applicable laws</li>
                                <li>
                                  Cannot cause nuisance or property damage
                                </li>
                                <li>May require identity verification</li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                5. ELECTRONIC SIGNATURE AGREEMENT
                              </h4>
                              <p className="mb-2">You agree that:</p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  Account creation and registration may be
                                  conducted electronically
                                </li>
                                <li>
                                  Electronic signatures have same legal validity
                                  as handwritten signatures
                                </li>
                                <li>
                                  You waive challenges based on electronic
                                  signature process
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                6. PAYMENT AND CHARGES
                              </h4>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  Fees for Digital Platform Ecosystem services
                                  listed at designated webpage
                                </li>
                                <li>
                                  DayDaCo may change fees with 14 days advance
                                  notice
                                </li>
                                <li>
                                  All charges are final and non-refundable
                                </li>
                                <li>
                                  Payment processing through third-party
                                  processors (e.g., Stripe)
                                </li>
                                <li>You're responsible for all taxes</li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                7. DISCLAIMERS; LIMITATION OF LIABILITY;
                                RELEASE; INDEMNITY
                              </h4>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                Disclaimer of Warranties
                              </h5>
                              <p className="font-semibold mb-2">
                                THE DIGITAL PLATFORM ECOSYSTEM IS PROVIDED "AS
                                IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY
                                KIND.
                              </p>
                              <p className="mb-2">
                                DayDaCo makes no warranties regarding:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>Accuracy, reliability, or timeliness</li>
                                <li>Uninterrupted or secure service</li>
                                <li>
                                  Quality or safety of third-party providers
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                Limitation of Liability
                              </h5>
                              <p className="font-semibold mb-2">
                                DayDaCo shall NOT be liable for:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>Lost profits, goodwill, or data</li>
                                <li>
                                  Special, incidental, indirect, or
                                  consequential damages
                                </li>
                                <li>
                                  Personal injury or health-related issues
                                </li>
                                <li>Third-party provider actions</li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                Time Limitation
                              </h5>
                              <p className="font-semibold">
                                Any claim must be asserted within 180 days after
                                the claim accrues.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">Release</h5>
                              <p>
                                You release DayDaCo from claims arising from
                                disputes with other users.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">Indemnity</h5>
                              <p className="mb-2">
                                You agree to indemnify and hold harmless DayDaCo
                                from claims arising from:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  Your use of the Digital Platform Ecosystem
                                </li>
                                <li>Your breach of Terms of Service</li>
                                <li>Your violation of third-party rights</li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                8. OTHER PROVISIONS
                              </h4>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (a) Defined Terms
                              </h5>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  <strong>"Sole Discretion":</strong> DayDaCo's
                                  unilateral, subjective, absolute discretion
                                  without regard to reasonableness
                                </li>
                                <li>
                                  <strong>"Including":</strong> Means
                                  "including, but not limited to"
                                </li>
                                <li>
                                  <strong>
                                    "Dispute, controversy, or claim":
                                  </strong>{' '}
                                  Any action based in law, equity, contract,
                                  tort, or otherwise
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (b) Choice of Law
                              </h5>
                              <p>
                                Governed by laws of the state where dispute
                                arises, excluding conflict-of-law rules.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (c) Choice of Forum
                              </h5>
                              <p>
                                Disputes brought exclusively in state and
                                federal courts where dispute arose (subject to
                                arbitration agreement).
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (d) No Joint Venture
                              </h5>
                              <p>
                                No partnership, agency, or joint venture
                                relationship created.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">(e) Notice</h5>
                              <p>
                                DayDaCo may give notice through the platform,
                                email, phone, or mail. Notice effective 48 hours
                                after mailing or immediately if electronic.
                              </p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">(f) Taxes</h5>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>All charges exclusive of taxes</li>
                                <li>
                                  You responsible for determining and paying
                                  applicable taxes
                                </li>
                                <li>
                                  DayDaCo may report tax information to
                                  authorities
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">
                                (g) General
                              </h5>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  You cannot assign rights without DayDaCo's
                                  written approval
                                </li>
                                <li>DayDaCo may assign without consent</li>
                                <li>No third-party beneficiaries</li>
                                <li>No waiver of DayDaCo's rights</li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                9. NOTICE FOR CALIFORNIA USERS
                              </h4>
                              <p className="mb-2">
                                California residents may contact:
                              </p>
                              <ul className="list-disc ml-5 space-y-1">
                                <li>
                                  <strong>
                                    California Department of Consumer Affairs
                                  </strong>
                                </li>
                                <li>
                                  <strong>Address:</strong> 1625 North Market
                                  Blvd., Suite N 112, Sacramento, CA 95834
                                </li>
                                <li>
                                  <strong>Phone:</strong> (916) 445-1254 or
                                  (800) 952-5210
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-bold text-base mb-2">
                                10. CONTACT INFORMATION
                              </h4>
                              <p className="mb-2">
                                For questions, concerns, or suggestions about
                                the Terms of Service:
                              </p>
                              <p>
                                <strong>Email:</strong> legal@daydaco.com
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col space-y-2">
                          <button
                            type="button"
                            onClick={scrollUp}
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded border flex items-center justify-center transition-colors duration-200"
                            aria-label="Scroll up"
                          >
                            <i className="fa-solid fa-chevron-up text-gray-600 text-sm" />
                          </button>
                          <button
                            type="button"
                            onClick={scrollDown}
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded border flex items-center justify-center transition-colors duration-200"
                            aria-label="Scroll down"
                          >
                            <i className="fa-solid fa-chevron-down text-gray-600 text-sm" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {error && <div className="text-sm text-red-600">{error}</div>}

                  <div className="flex justify-between gap-3 mb-6">
                    {step < 4 ? (
                      <>
                        {step > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setStep((prev) => prev - 1)}
                            className="w-24"
                            disabled={loading}
                          >
                            <i className="fa-solid fa-arrow-left mr-2" />
                            Back
                          </Button>
                        )}
                        <Button
                          type="button"
                          onClick={handleNext}
                          className={`${step === 1 ? 'w-full' : 'flex-1'} bg-[#1C64F2] text-white disabled:bg-[#E5E7EB] disabled:text-[#111928]`}
                          disabled={
                            loading ||
                            (step === 1 &&
                              (!form.watch('firstName') ||
                                !form.watch('lastName') ||
                                !form.watch('email') ||
                                !form.watch('phoneNumber') ||
                                !form.watch('password') ||
                                (!isTeamInvite &&
                                  (form.watch('stakeholderTypes') || [])
                                    .length === 0))) ||
                            (step === 2 &&
                              (!form.watch('companyName') ||
                                !form.watch('corporateAddress') ||
                                !form.watch('companyType') ||
                                ((form.watch('companyType') ===
                                  CompanyType.C_CORP ||
                                  form.watch('companyType') ===
                                    CompanyType.S_CORP) &&
                                  !form.watch('ccn')) ||
                                ((form.watch('companyType') ===
                                  CompanyType.LLC ||
                                  form.watch('companyType') ===
                                    CompanyType.PARTNERSHIP) &&
                                  !form.watch('eid')) ||
                                (form.watch('companyType') ===
                                  CompanyType.INDIVIDUAL &&
                                  (!form.watch('licenseNumber') ||
                                    !form.watch('permitNumber') ||
                                    !form.watch('zoningNumber'))))) ||
                            (step === 3 && !isPaymentFormReady)
                          }
                        >
                          {loading ? (
                            <>
                              <i className="fa-duotone fa-solid fa-spinner-third fa-spin mr-2" />
                              {step === 3
                                ? 'Validating Payment...'
                                : isTeamInvite
                                  ? 'Setting up your team account...'
                                  : 'Loading...'}
                            </>
                          ) : (
                            <>
                              {step === 3 ? (
                                <>
                                  <i className="fa-solid fa-credit-card mr-2" />
                                  Validate Payment & Continue
                                </>
                              ) : (
                                'Next'
                              )}
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <div className="w-full bg-white/90 backdrop-blur-sm rounded-lg">
                        <div className="flex justify-between items-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-20 h-10"
                            onClick={() => setStep((prev) => prev - 1)}
                            disabled={loading}
                          >
                            <i className="fa-solid fa-arrow-left mr-1" />
                            Back
                          </Button>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              className="w-20 h-10 bg-green-500 hover:bg-green-600 text-white"
                              disabled={loading || !hasScrolledToBottom}
                              onClick={handleAcceptTerms}
                            >
                              {loading ? (
                                <i className="fa-duotone fa-solid fa-spinner-third fa-spin" />
                              ) : (
                                'I Agree'
                              )}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="w-20 h-10 bg-red-500 hover:bg-red-600 text-white"
                              disabled={!hasScrolledToBottom}
                              onClick={() => {
                                Sentry.addBreadcrumb({
                                  message: 'User clicked decline terms button',
                                  category: 'registration',
                                  level: 'info',
                                  data: {
                                    email: form.getValues('email'),
                                    step: 'terms_declined',
                                  },
                                })
                                setRejectionModal(true)
                              }}
                            >
                              DECLINE
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </div>

        <VerifyEmailModal
          isOpen={verifyEmailModal}
          email={registrationEmail}
          onVerificationSuccess={handleEmailVerificationSuccess}
          onClose={handleEmailVerificationClose}
        />

        <TermsConfirmationModal
          isOpen={confirmationModal}
          loading={loading}
          onConfirm={handleConfirmAcceptance}
          onClose={() => setConfirmationModal(false)}
        />

        <AcceptModal
          isOpen={successModal}
          onClose={() => setSuccessModal(false)}
          autoRedirect={false}
        />

        <RejectModal
          isOpen={rejectionModal}
          onClose={() => setRejectionModal(false)}
        />
      </div>
    </div>
  )
}

// Wrap with Sentry ErrorBoundary for comprehensive error catching
const RegisterWithErrorBoundary = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-red-500 text-6xl mb-4">
              <i className="fa-solid fa-exclamation-triangle" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              We're sorry, but something went wrong during registration. Our
              team has been notified.
            </p>
            <div className="space-y-3">
              <button
                onClick={resetError}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Try Again
              </button>
              <a
                href="/login"
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors inline-block"
              >
                Go to Login
              </a>
            </div>
          </div>
        </div>
      </div>
    )}
    beforeCapture={(scope, error) => {
      // Add additional context before sending to Sentry
      scope.setTag('error_boundary', 'registration_page')
      scope.setContext('registration_info', {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
      })
    }}
  >
    <Register />
  </Sentry.ErrorBoundary>
)

export { RegisterWithErrorBoundary as default }
